/**
 * This module provides functionality to block specific services/features for users from restricted countries.
 */
(function ($) {
  "use strict";

  // Create a namespace for country blocking functionality
  window.VeriffGeo = window.VeriffGeo || {};

  // Restricted countries and regions list
  VeriffGeo.blockedCountries = [
    "IR", // Iran
    "KP", // North Korea
    "SY", // Syria
    "RU", // Russia
    "BY", // Belarus
    "VE", // Venezuela
    "CU", // Cuba
    "AF", // Afghanistan
    "CN", // China
    "HK", // Hong Kong
    "MO", // Macau
  ];

  // Regions blocked inside an otherwise open country. The /geoip/ endpoint
  // returns the ISO 3166-2 subdivision for Ukraine (WM-1413: Crimea,
  // Donetsk and Luhansk are sanctioned).
  VeriffGeo.blockedRegions = {
    UA: ["UA-43", "UA-14", "UA-09"], // Crimea, Donetsk, Luhansk
  };

  // Store user's location data
  VeriffGeo.userCountry = "";
  VeriffGeo.userRegion = "";
  VeriffGeo.isBlocked = false;
  VeriffGeo.isLocationDetected = false;

  /**
   * Check if a user should be blocked based on their location
   * @returns {boolean} True if user should be blocked, false otherwise
   */
  VeriffGeo.isUserBlocked = function () {
    if (VeriffGeo.blockedCountries.includes(VeriffGeo.userCountry)) {
      return true;
    }
    var regions = VeriffGeo.blockedRegions[VeriffGeo.userCountry];
    if (regions && VeriffGeo.userRegion && regions.includes(VeriffGeo.userRegion)) {
      return true;
    }
    return false;
  };

  /**
   * Handle visibility of menu items based on the user's country
   * - Show Privacy Choices only for users in Mexico (MX) and the United States (US)
   * - Hide Cookie Policy for users in Mexico (MX) and the United States (US)
   */
  VeriffGeo.handlePrivacyChoicesVisibility = function () {
    // List of countries where Privacy Choices should be visible and Cookie Policy hidden
    var specialCountries = ["US", "MX"];

    // Check if a user's country is in the special countries list
    var isInSpecialCountry = specialCountries.includes(VeriffGeo.userCountry);

    // Hide the Privacy Choices menu item if not in special countries
    if (!isInSpecialCountry) {
      $(".privacy-choices-option").css("display", "none");
    }

    // Hide the Cookie Policy menu item if in special countries
    if (isInSpecialCountry) {
      $(".cookie-policy-option").css("display", "none");
    }
  };

  /**
   * Browser-side cache of the last geo answer.
   *
   * A visitor's country effectively never changes within a day, so asking
   * the server once per page load only burns server time. The answer is
   * kept in localStorage for 24 hours and reused.
   *
   * Privacy (GDPR/ePrivacy): only the values the page functionally needs
   * are stored — country, region, timestamp — never the IP or coordinates.
   * This storage is strictly necessary for the privacy-choices menu and
   * regional compliance itself, is not used for tracking, and expires on
   * its own.
   */
  var GEO_CACHE_KEY = "veriffGeoV1";
  var GEO_CACHE_TTL = 24 * 60 * 60 * 1000;

  function readGeoCache() {
    try {
      var cached = JSON.parse(window.localStorage.getItem(GEO_CACHE_KEY));
      if (
        cached &&
        typeof cached.t === "number" &&
        Date.now() - cached.t < GEO_CACHE_TTL
      ) {
        return cached;
      }
    } catch (e) {
      // Storage unavailable or corrupt entry - fall through to the network.
    }
    return null;
  }

  function writeGeoCache(country, region) {
    try {
      window.localStorage.setItem(
        GEO_CACHE_KEY,
        JSON.stringify({ c: country, r: region, t: Date.now() })
      );
    } catch (e) {
      // Storage unavailable (private mode, quota) - caching is best-effort.
    }
  }

  /**
   * Apply a detection result and notify listeners.
   * @param {string} country - ISO country code ("" when unknown)
   * @param {string} region - Region ISO code ("" unless relevant)
   * @param {Function} callback - Function to run after detection (optional)
   */
  function applyGeoResult(country, region, callback) {
    VeriffGeo.userCountry = country || "";
    VeriffGeo.userRegion = region || "";
    VeriffGeo.isBlocked = VeriffGeo.isUserBlocked();
    VeriffGeo.isLocationDetected = true;

    if (typeof callback === "function") {
      callback(VeriffGeo.isBlocked);
    }

    $(document).trigger("veriff:countryDetected", [
      VeriffGeo.userCountry,
      VeriffGeo.isBlocked,
    ]);
  }

  /**
   * Detect user's country, from the local 24h cache when possible,
   * otherwise via the site's own /geoip/ endpoint (local GeoLite2 lookup,
   * no third-party service).
   * @param {Function} callback - Function to run after detection (optional)
   */
  VeriffGeo.detectUserCountry = function (callback) {
    var cached = readGeoCache();
    if (cached) {
      applyGeoResult(cached.c, cached.r, callback);
      return;
    }

    $.ajax({
      // Relative on purpose: the veriff-geoip mu-plugin answers /geoip/ on
      // EVERY environment (prod, test, dev, multidevs). An absolute prod URL
      // here fails CORS from any other origin.
      url: "/geoip/",
      type: "POST",
      dataType: "json",
      success: function (response) {
        var country = "";
        var region = "";

        if (response && response.country) {
          country = response.country; // ISO country code

          // If it's Ukraine, store region information if available
          if (response.country === "UA" && response.region) {
            region = response.region;
          }
        }

        // Only a real answer is worth remembering; an empty one may be a
        // transient lookup failure and should be retried on the next page.
        if (country) {
          writeGeoCache(country, region);
        }
        applyGeoResult(country, region, callback);
      },
      error: function () {
        // Fallback - if API call fails
        console.log(
          "Geolocation API failed, proceeding without country blocking"
        );
        VeriffGeo.isLocationDetected = true;

        // Run callback if provided
        if (typeof callback === "function") {
          callback(false);
        }

        // Trigger event with failure status
        $(document).trigger("veriff:countryDetectionFailed");
      },
    });
  };

  /**
   * Initialize the country blocking functionality
   * @param {Object} options - Configuration options
   */
  VeriffGeo.init = function (options) {
    const settings = $.extend(
      {
        detectOnLoad: true,
        alertElementId: "country-block-alert",
        restrictedSelectors: [],
      },
      options
    );

    // Store settings for later use
    VeriffGeo.settings = settings;

    // Detect country immediately if specified
    if (settings.detectOnLoad) {
      VeriffGeo.detectUserCountry();
    }

    return this;
  };

  $(document).ready(function () {
    VeriffGeo.init();
  });

  // Add event listener for when country detection is complete
  $(document).on("veriff:countryDetected", function () {
    VeriffGeo.handlePrivacyChoicesVisibility();
  });
})(jQuery);
